import React, { useState } from 'react';
import { Form, Input, InputNumber, DatePicker, Upload, Button, Tooltip, Row, Col } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import SystemModal from 'components/Shared/Modal';
import { InfoIcon, ReceiptIcon, UploadIcon } from 'components/Shared/Icons';
import Can from 'config/Can';
import useTranslation from 'utils/hooks/useTranslation';
import useIsFormValid from 'utils/hooks/useIsFormValid';
import notify from 'utils/notificationUtils';
import { MAX_UPLOAD_SIZE_BYTES } from 'constants.js';
import { useAddWireLoad } from 'services/reactQueryHooks/wallet';
import { getFileSha256Hex, handleAmountKeyDown, handleAmountPaste } from './helper';

import styles from './style.module.scss';

const KEY_PREFIX = 'corporateAccounts.wallet.dashboard.addFunds';
const PROOF_OF_TRANSFER_ACCEPT = '.pdf,.png,.jpg,.jpeg';

const fieldValidationConfig = [
  { name: 'amountSent', required: true },
  { name: 'transferDate', required: true },
];

const formatFileSize = bytes => `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Step 2 of the bank transfer flow: the customer declares what they sent so the payment can be
// matched. `wireReference` is the reference the Wallet Service issued for this wallet — it is
// shown rather than typed, and is what gets submitted. `currency` is the wallet's own currency
// ('gbp' | 'eur'), for analytics. `onBack` returns to step 1; `onSubmitted` reports the declared
// amount once the wire load has been accepted, so the dashboard can report it back.
const ConfirmTransferModal = ({
  visible,
  onClose,
  onBack,
  onSubmitted,
  currencySymbol,
  currency,
  walletId,
  wireReference,
}) => {
  const { translateText } = useTranslation();
  const [form] = Form.useForm();
  const { isFormValid, validateForm } = useIsFormValid(form, fieldValidationConfig);
  const [proofFile, setProofFile] = useState(null);
  // null until the customer answers; the answer itself is optional, so it never gates Submit.
  const [isReferenceIncluded, setIsReferenceIncluded] = useState(null);
  const [senderName, setSenderName] = useState('');
  const { mutate: addWireLoad, isLoading: isSubmitting } = useAddWireLoad({ currency });

  const resetModal = () => {
    form.resetFields();
    setProofFile(null);
    setIsReferenceIncluded(null);
    setSenderName('');
  };

  const handleCancel = () => {
    resetModal();
    onClose();
  };

  const handleBack = () => {
    resetModal();
    onBack();
  };

  const handleFinish = async ({ amountSent }) => {
    const sha256 = await getFileSha256Hex(proofFile);
    const declaredAmountMinor = Math.round(amountSent * 100);

    addWireLoad(
      {
        declaredAmountMinor,
        // The reference is the one we issued, not something the customer types
        reference: wireReference ?? null,
        // What the customer says they actually quoted, plus whatever helps us find a transfer
        // that went out without the reference.
        referenceIncluded: isReferenceIncluded,
        senderName: senderName.trim() || null,
        walletId,
        fileName: proofFile.name,
        contentType: proofFile.type,
        sizeBytes: proofFile.size,
        sha256,
        file: proofFile,
      },
      {
        onSuccess: () => {
          resetModal();
          if (onSubmitted) {
            onSubmitted(declaredAmountMinor);
          }
          onClose();
        },
      },
    );
  };

  const handleRemoveProof = () => {
    setProofFile(null);
  };

  const handleBeforeUpload = file => {
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      notify({
        type: 'error',
        message: translateText('corporateAccounts.import.maxFileSize.error'),
      });
      return false;
    }

    setProofFile(file);
    return false;
  };

  const label = key => translateText(`${KEY_PREFIX}.${key}`) + ' *';

  return (
    <SystemModal
      title={
        <>
          <div className={styles.dashboardModalStep}>
            {translateText(`${KEY_PREFIX}.confirmTransferModal.step`)}
          </div>
          <span className={styles.dashboardModalTitle}>
            {translateText(`${KEY_PREFIX}.confirmTransferModal.title`)}
          </span>
        </>
      }
      titleLevel={5}
      width={440}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      bodyStyle={{ padding: '20px' }}
      className={`${styles.dashboardModal} ${styles.confirmTransferModal}`}
      destroyOnClose
      centered
      data-testid="confirmTransferModal"
    >
      <div className={styles.confirmTransferCopy}>
        {translateText(`${KEY_PREFIX}.confirmTransferModal.copy`)}
      </div>
      <Form
        form={form}
        layout="vertical"
        colon={false}
        onFieldsChange={validateForm}
        onFinish={handleFinish}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="amountSent"
              label={label('confirmTransferModal.amountSent')}
              required={false}
              rules={[
                {
                  required: true,
                  message: translateText(`${KEY_PREFIX}.confirmTransferModal.amountSentRequired`),
                },
              ]}
            >
              <InputNumber
                min={0.01}
                controls={false}
                placeholder={`${currencySymbol}0.00`}
                className={styles.confirmTransferInput}
                data-testid="confirmTransferModalAmount"
                onKeyDown={handleAmountKeyDown}
                onPaste={handleAmountPaste}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="transferDate"
              label={label('confirmTransferModal.transferDate')}
              required={false}
              rules={[
                {
                  required: true,
                  message: translateText(`${KEY_PREFIX}.confirmTransferModal.transferDateRequired`),
                },
              ]}
            >
              <DatePicker
                className={styles.confirmTransferInput}
                disabledDate={date => date && date.isAfter(moment(), 'day')}
                data-testid="confirmTransferModalDate"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div className={styles.confirmTransferReference}>
        <div className={styles.confirmTransferReferenceRow}>
          <span className={styles.bankDetailLabel}>
            {translateText(`${KEY_PREFIX}.confirmTransferModal.paymentReference`)}
          </span>
          <span
            // Same reason as the step 1 details: the reference must survive page translation
            className={classNames(styles.bankDetailValue, 'notranslate')}
            translate="no"
            data-testid="confirmTransferModalReference"
          >
            {wireReference}
          </span>
        </div>
        <div className={styles.confirmTransferReferenceQuestion}>
          {translateText(`${KEY_PREFIX}.confirmTransferModal.referenceIncludedQuestion`)}
        </div>
        <div className={styles.confirmTransferToggle} role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={isReferenceIncluded === true}
            className={classNames(styles.confirmTransferToggleOption, {
              [styles.confirmTransferToggleSelected]: isReferenceIncluded === true,
            })}
            onClick={() => setIsReferenceIncluded(true)}
            data-testid="confirmTransferModalReferenceYes"
          >
            {translateText('corporateAccounts.common.yes')}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isReferenceIncluded === false}
            className={classNames(styles.confirmTransferToggleOption, {
              [styles.confirmTransferToggleSelected]: isReferenceIncluded === false,
            })}
            onClick={() => setIsReferenceIncluded(false)}
            data-testid="confirmTransferModalReferenceNo"
          >
            {translateText('corporateAccounts.common.no')}
          </button>
        </div>
      </div>

      {isReferenceIncluded === false && (
        <>
          <div
            className={styles.confirmTransferReferenceWarning}
            data-testid="confirmTransferModalReferenceWarning"
          >
            <span className={styles.confirmTransferReferenceWarningIcon}>
              <InfoIcon />
            </span>
            <span>
              {translateText(`${KEY_PREFIX}.confirmTransferModal.referenceMissingWarning`)}
            </span>
          </div>
          <div className={styles.confirmTransferFieldLabel}>
            {translateText(`${KEY_PREFIX}.confirmTransferModal.senderName`)}
          </div>
          <Input
            value={senderName}
            onChange={event => setSenderName(event.target.value)}
            placeholder={translateText(`${KEY_PREFIX}.confirmTransferModal.senderNamePlaceholder`)}
            className={classNames(styles.confirmTransferInput, styles.confirmTransferSenderName)}
            data-testid="confirmTransferModalSenderName"
          />
        </>
      )}

      <div className={styles.confirmTransferFieldLabel}>
        {label('confirmTransferModal.proofOfTransfer')}
      </div>
      {proofFile ? (
        <div
          className={styles.confirmTransferProofAttached}
          data-testid="confirmTransferModalProofAttached"
        >
          <span className={styles.confirmTransferProofIcon}>
            <ReceiptIcon />
          </span>
          <div className={styles.confirmTransferProofDetails}>
            <div className={styles.confirmTransferProofName}>{proofFile.name}</div>
            <div className={styles.confirmTransferProofSize}>{formatFileSize(proofFile.size)}</div>
          </div>
          <span
            className={styles.confirmTransferProofRemove}
            onClick={handleRemoveProof}
            data-testid="confirmTransferModalProofRemove"
          >
            {translateText('corporateAccounts.common.remove')}
          </span>
        </div>
      ) : (
        <Upload
          accept={PROOF_OF_TRANSFER_ACCEPT}
          beforeUpload={handleBeforeUpload}
          showUploadList={false}
          className={styles.confirmTransferProofUploadWrapper}
          data-testid="confirmTransferModalProofUpload"
        >
          <div className={styles.confirmTransferProofDropzone}>
            <span className={styles.confirmTransferProofDropzoneIcon}>
              <UploadIcon />
            </span>
            <div className={styles.confirmTransferProofDropzoneTitle}>
              {translateText(`${KEY_PREFIX}.confirmTransferModal.proofOfTransferAttach`)}
            </div>
            <div className={styles.confirmTransferProofDropzoneSubtitle}>
              {translateText(`${KEY_PREFIX}.confirmTransferModal.proofOfTransferFileTypes`)}
            </div>
          </div>
        </Upload>
      )}

      <div className={styles.confirmTransferActions}>
        <Button
          className={styles.confirmTransferBack}
          onClick={handleBack}
          data-testid="confirmTransferModalBack"
        >
          {translateText('corporateAccounts.actions.back')}
        </Button>
        <Can I="submit" a="Wallet" passThrough>
          {allowed => (
            <Tooltip
              title={
                allowed ? null : translateText(`${KEY_PREFIX}.confirmTransferModal.submitDisabled`)
              }
            >
              <span className={styles.confirmTransferSubmitWrap}>
                <Button
                  type="primary"
                  block
                  loading={isSubmitting}
                  className={styles.confirmTransferSubmit}
                  disabled={!allowed || !isFormValid || !proofFile}
                  onClick={() => form.submit()}
                  data-testid="confirmTransferModalSubmit"
                >
                  {translateText(`${KEY_PREFIX}.confirmTransferModal.submit`)}
                </Button>
              </span>
            </Tooltip>
          )}
        </Can>
      </div>
    </SystemModal>
  );
};

export default ConfirmTransferModal;
